import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import { electronApis } from '@/features/electron-api';
import type { SetupProgressEvent } from '@/features/electron-api/features/AxonMcpAPI';
import { AXON_MCP_SERVERS_QUERY_KEY } from '@/features/settings/sub-settings/settings-local-mcp.query';

type Progress = Record<string, string>;
export type SetupEnding = Exclude<SetupProgressEvent['state'], 'running'>;
type Endings = Record<string, SetupEnding>;

interface Session {
  closed: boolean;
  unsubscribe?: () => void;
}

/**
 * `ElectronAPI.addListener` keeps one listener per event type, so every hook
 * instance subscribing on its own would evict the others. One reference-counted
 * subscription feeds all of them.
 */
const store = {
  progress: {} as Progress,
  endings: {} as Endings,
  readers: new Set<() => void>(),
  queryClient: undefined as QueryClient | undefined,
  // Runs that ended after the snapshot was requested; their snapshot entries
  // are stale and must not resurrect a phantom "setting-up" row.
  ended: new Set<string>(),
  open: undefined as Session | undefined,
  closeScheduled: false,
};

function publish(next: Progress, endings = store.endings) {
  store.progress = next;
  store.endings = endings;
  for (const reader of store.readers) reader();
}

function handleEvent(event: SetupProgressEvent) {
  if (event.state === 'running') {
    publish({ ...store.progress, [event.slug]: event.line });
    return;
  }
  store.ended.add(event.slug);
  const next = { ...store.progress };
  delete next[event.slug];
  publish(next, { ...store.endings, [event.slug]: event.state });
  void store.queryClient?.invalidateQueries({
    queryKey: AXON_MCP_SERVERS_QUERY_KEY,
  });
}

function openSubscription() {
  const session: Session = { closed: false };
  store.open = session;
  store.ended.clear();

  // Seed from the main-process snapshot so a page opened mid-run shows
  // status immediately instead of waiting for the next 30s report.
  // Pushed events merge over the snapshot (they are always newer).
  void electronApis.axonMcp.getSetupProgress().then((snapshot) => {
    if (session.closed) return;
    const seeded = { ...snapshot };
    for (const slug of store.ended) delete seeded[slug];
    publish({ ...seeded, ...store.progress });
  });

  void electronApis.axonMcp.onSetupProgress(handleEvent).then((unsubscribe) => {
    session.unsubscribe = unsubscribe;
    // If a newer session has opened since, it already replaced this listener
    // in the shared slot, and unsubscribing now would remove the newer one.
    if (session.closed && !store.open) unsubscribe();
  });
}

function closeSubscription() {
  if (store.open) {
    store.open.closed = true;
    store.open.unsubscribe?.();
  }
  store.open = undefined;
  store.progress = {};
  store.endings = {};
}

// The old module copy's late unsubscribe would empty the reloaded copy's listener
import.meta.hot?.dispose(closeSubscription);

function subscribe(queryClient: QueryClient, reader: () => void) {
  store.readers.add(reader);
  store.queryClient = queryClient;
  if (!store.open) openSubscription();

  return () => {
    store.readers.delete(reader);
    if (store.readers.size > 0 || store.closeScheduled) return;
    // Wait a tick so a StrictMode remount reuses the subscription.
    store.closeScheduled = true;
    queueMicrotask(() => {
      store.closeScheduled = false;
      if (store.readers.size === 0) closeSubscription();
    });
  };
}

/**
 * Live setup-run state, keyed by server slug. A slug is present only while its
 * setup run is in flight, with the latest raw progress line as its value. On
 * completion the slug is dropped and the servers query is invalidated so the
 * persisted `lastSetup` stamp refreshes.
 */
export function useSetupProgress(): Progress {
  const queryClient = useQueryClient();
  const subscribeToStore = useCallback(
    (reader: () => void) => subscribe(queryClient, reader),
    [queryClient]
  );
  return useSyncExternalStore(subscribeToStore, () => store.progress);
}

/** How the slug's last setup run ended, if one ended while this page was open */
export function useSetupEnding(slug: string): SetupEnding | undefined {
  const queryClient = useQueryClient();
  const subscribeToStore = useCallback(
    (reader: () => void) => subscribe(queryClient, reader),
    [queryClient]
  );
  return useSyncExternalStore(subscribeToStore, () => store.endings[slug]);
}
